// Types for the tax calculation engine

export type FilingStatus = 'S' | 'MFJ' | 'MFS' | 'HOH';

export interface TaxScenarioInput {
  name: string;
  
  // Filing & personal
  filingStatus: FilingStatus;
  state: string;
  taxYear: number;
  tp65Plus: boolean;
  sp65Plus: boolean;
  tp55Plus: boolean;
  sp55Plus: boolean;
  tpBlind: boolean;
  spBlind: boolean;
  nyTp59Half: boolean;
  nySp59Half: boolean;
  /** Birth years, typed on Filing & Personal behind the `roth_plan` flag. The multi-year Roth plan
   *  reads the RMD start age and the Medicare year from them. The engine ignores both. */
  tpBirthYear?: number;
  spBirthYear?: number;

  // Income
  wages: number;
  spouseWages: number;
  taxExemptInterest: number;
  interestIncome: number;
  ordinaryDividends: number;
  qualifiedDividends: number;
  iraDistributionConversion: number;
  rothConversionAmount: number;
  grossSocialSecurity: number;
  pensionSum: number;
  militaryRetirementPension: number;
  nonQualifiedAnnuityIncome: number;
  shortTermCapitalGains: number;
  longTermCapitalGains: number;
  otherIncome: number; // Sch 1 Part I

  // Schedule C
  schedCGrossReceipts: number;
  schedCTotalExpenses: number;
  spouseSchedCGrossReceipts: number;
  spouseSchedCTotalExpenses: number;

  // Schedule E
  rentalActive: boolean;
  rentalIncome: number;
  rentalTotalExpenses: number;
  k1OrdinaryBusinessIncome: number;
  k1GuaranteedPayments: number;
  k1RentalRealEstateIncome: number;
  k1OtherIncome: number;

  // Schedule A - Itemized deductions
  medicalExpenses: number;
  saltStateLocal: number;
  saltRealEstate: number;
  saltPersonalProperty: number;
  homeMortgageInterest: number;
  mortgagePoints: number;
  investmentInterest: number;
  cashGiftsPublicCharity: number;
  nonCashPropertyPublicCharity: number;
  propertyGiftsPrivateFoundation: number;
  capitalGainPropertyPublicCharity: number;
  capitalGainPropertyPrivateFoundation: number;
  otherItemizedDeductions: number;

  // Schedule SE
  w2SocialSecurityWages: number;
  spouseW2SocialSecurityWages: number;

  // Schedule 1 adjustments
  selfEmployedHealthInsurance: number;
  selfEmployedRetirement: number;
  educatorExpenses: number;
  hsaDeduction: number;
  traditionalIRADeduction: number;
  studentLoanInterest: number;
  otherAdjustments: number;

  // Schedule 2
  amtAdjustment: number;
  excessAPTCRepayment: number;
  otherAdditionalTaxes: number;
  schedCAmtDepreciation: number;
  k1AmtOrdinaryIncomeAdj: number;
  k1AmtDepreciationAdj: number;
  k1Amt179Adj: number;
  k1AmtPassiveLossAdj: number;
  k1AmtDepletionAdj: number;
  k1AmtIDCAdj: number;
  k1AmtOtherAdj: number;
  optionalPassiveActivityAddback: number;

  // Schedule 3 - Credits
  foreignTaxCredit: number;
  childDependentCareCredit: number;
  educationCredits: number;
  saversCredit: number;
  energyCredits: number;
  earnedIncomeCredit: number;
  additionalChildTaxCredit: number;
  aotcRefundable: number;
  netPremiumTaxCredit: number;
  amountPaidWithExtension: number;
  otherRefundableCredits: number;
  federalWithholding: number;
  estimatedTaxPayments: number;
  /** State income tax withheld (W-2 box 17). Nets against stateIncomeTax; never touches the federal cascade. */
  stateWithholding: number;

  // Flags
  applyACA: boolean;
  applyNIIT: boolean;
  numberOfQualifyingChildren: number;

  // QBI
  qbiW2Wages: number;
  qbiUBIA: number;
  qbiSSTB: boolean;

  // Alternative investment
  alternativeInvestmentAmount: number;
  /** Which fund's PPM Exhibit schedule the alt cash flows follow. Undefined = current default. */
  altFundVintageId?: string;
  /** Kept for now: when true the alt returns capital only (principal back in Year 6, no distributions).
   *  The deduction cascade is unaffected. */
  hpeConservativeMode?: boolean;

  // Growth & Projection Assumptions
  rothGrowthRate: number;
  rothGrowthYears: number;
  /** Selector for Roth conversion growth projection. Undefined = legacy straight compound. */
  rothGrowthModel?: import('./rothGrowth').RothGrowthModel;
  /** Legacy inputs, ignored by the engine: alt cash flows follow the fund exhibit (altFundVintageId). */
  hpeInterestRate: number;
  hpeInterestYears: number;
  hpeDeductionMultiplier: number;

  // Pension split (NY/MA state calc)
  govtPensionTP: number;
  govtPensionSP: number;
  nonGovtPensionTP: number;
  nonGovtPensionSP: number;

  // UI state persisted alongside input. Engine ignores this field.
  uiState?: VariantUIState;
}

export interface VariantUIState {
  autoMaxAlt?: boolean;
  autoSolveRoth?: boolean;
  /** Multi-year Roth plan settings (`roth_plan` flag). Absent on every strategy saved without one. */
  rothPlan?: import('./rothPlan').RothPlanSettings;
}

export interface TaxResults {
  // Federal
  totalIncome: number;
  adjustedGrossIncome: number;
  standardDeduction: number;
  itemizedDeductions: number;
  bbbAmount: number;
  totalDeduction: number;
  qbiDeduction: number;
  taxableIncome: number;
  preferentialIncome: number;
  ordinaryTaxableIncome: number;
  /** Federal rate on the last dollar of ordinaryTaxableIncome (a bracket top counts as inside it). Absent on rows saved before it existed. */
  ordinaryBracketRate?: number;
  /** Bottom of that bracket. */
  ordinaryBracketFloor?: number;
  /** Top of that bracket; null in the top bracket. */
  ordinaryBracketCeiling?: number | null;
  ordinaryIncomeTax: number;
  capitalGainsDividendsTax: number;
  taxBeforeCredits: number;
  childTaxCredit: number;
  totalNonrefundableCredits: number;
  schedule2AdditionalTaxes: number;
  selfEmploymentTax: number;
  niit: number;
  additionalMedicareTax: number;
  amtOwed: number;
  totalFederalTax: number;
  totalRefundableCredits: number;
  netTaxOwedOrRefund: number;

  // State
  stateTaxableIncome: number;
  stateIncomeTax: number;
  /** stateIncomeTax − stateWithholding. Positive = owed, negative = refund. */
  netStateTaxOwedOrRefund: number;

  // Combined
  totalTaxLiability: number;
  effectiveTaxRate: number;

  // Medicare IRMAA
  medicarePartBPremium: number;
  medicarePartDPremium: number;
  totalMedicarePremium: number;

  // Totals
  alternativeInvestmentCapital: number;
  totalEconomicCost: number;

  // Social Security
  taxableSocialSecurity: number;

  // Schedule details
  schedCNetProfit: number;
  schedDNetCapitalGain: number;
  schedENetRentalIncome: number;
  schedEK1Total: number;
  totalSETax: number;
  halfSETax: number;

  // Roth Growth Engine
  rothFutureValue: number;
  estimatedFutureTaxAvoided: number;

  // HPE Investment Return Engine
  hpeTotalInterest: number;
  hpeNetCashReturned: number;
  hpeFutureValue: number;
  hpeImpliedDeduction: number;
  hpeMaxInvestment: number;
  hpeRemainingCapacity: number;
  hpeIRR: number;
  /** Year 0 = -investment; Years 1..N = the fund's hypothetical distributions scaled to it. */
  hpeCashFlows: number[];
  /** Pre-tax multiple on invested capital (total cash to client / investment), per the fund exhibit. */
  hpeMoic: number;
  hpeFundVintageId: string;
  hpeFundLabel: string;
}

export interface TaxScenario {
  id: string;
  clientId: string;
  /**
   * Lineage root — the client_id of the original client this scenario descends from.
   * Equals clientId for brand-new clients; inherited from the source when duplicated.
   * Used to cluster duplicates next to their original on the dashboard.
   */
  rootClientId: string;
  input: TaxScenarioInput;
  results: TaxResults;
  createdAt: string;
  updatedAt: string;
  isDraft?: boolean;
  sortOrder?: number;
  /**
   * DB row's user_id — owner of the scenario.
   * Needed in org-scoped views so:
   * - the All Clients view can render "by {creator}" next to each client
   * - org managers editing an agent's client create new variants under the
   *   agent's id (not their own), keeping the client's scenarios grouped.
   */
  userId?: string;
}

export const DEFAULT_INPUT: TaxScenarioInput = {
  name: '',
  filingStatus: 'S',
  state: 'Missouri',
  taxYear: 2026,
  tp65Plus: false,
  sp65Plus: false,
  tp55Plus: false,
  sp55Plus: false,
  tpBlind: false,
  spBlind: false,
  nyTp59Half: false,
  nySp59Half: false,
  wages: 0,
  spouseWages: 0,
  taxExemptInterest: 0,
  interestIncome: 0,
  ordinaryDividends: 0,
  qualifiedDividends: 0,
  iraDistributionConversion: 0,
  rothConversionAmount: 0,
  grossSocialSecurity: 0,
  pensionSum: 0,
  militaryRetirementPension: 0,
  nonQualifiedAnnuityIncome: 0,
  shortTermCapitalGains: 0,
  longTermCapitalGains: 0,
  otherIncome: 0,
  schedCGrossReceipts: 0,
  schedCTotalExpenses: 0,
  spouseSchedCGrossReceipts: 0,
  spouseSchedCTotalExpenses: 0,
  rentalActive: true,
  rentalIncome: 0,
  rentalTotalExpenses: 0,
  k1OrdinaryBusinessIncome: 0,
  k1GuaranteedPayments: 0,
  k1RentalRealEstateIncome: 0,
  k1OtherIncome: 0,
  medicalExpenses: 0,
  saltStateLocal: 0,
  saltRealEstate: 0,
  saltPersonalProperty: 0,
  homeMortgageInterest: 0,
  mortgagePoints: 0,
  investmentInterest: 0,
  cashGiftsPublicCharity: 0,
  nonCashPropertyPublicCharity: 0,
  propertyGiftsPrivateFoundation: 0,
  capitalGainPropertyPublicCharity: 0,
  capitalGainPropertyPrivateFoundation: 0,
  otherItemizedDeductions: 0,
  w2SocialSecurityWages: 0,
  spouseW2SocialSecurityWages: 0,
  selfEmployedHealthInsurance: 0,
  selfEmployedRetirement: 0,
  educatorExpenses: 0,
  hsaDeduction: 0,
  traditionalIRADeduction: 0,
  studentLoanInterest: 0,
  otherAdjustments: 0,
  amtAdjustment: 0,
  excessAPTCRepayment: 0,
  otherAdditionalTaxes: 0,
  schedCAmtDepreciation: 0,
  k1AmtOrdinaryIncomeAdj: 0,
  k1AmtDepreciationAdj: 0,
  k1Amt179Adj: 0,
  k1AmtPassiveLossAdj: 0,
  k1AmtDepletionAdj: 0,
  k1AmtIDCAdj: 0,
  k1AmtOtherAdj: 0,
  optionalPassiveActivityAddback: 0,
  foreignTaxCredit: 0,
  childDependentCareCredit: 0,
  educationCredits: 0,
  saversCredit: 0,
  energyCredits: 0,
  earnedIncomeCredit: 0,
  additionalChildTaxCredit: 0,
  aotcRefundable: 0,
  netPremiumTaxCredit: 0,
  amountPaidWithExtension: 0,
  otherRefundableCredits: 0,
  federalWithholding: 0,
  estimatedTaxPayments: 0,
  stateWithholding: 0,
  applyACA: true,
  applyNIIT: true,
  numberOfQualifyingChildren: 0,
  qbiW2Wages: 0,
  qbiUBIA: 0,
  qbiSSTB: false,
  alternativeInvestmentAmount: 0,
  altFundVintageId: 'pfvi-v3.19',
  rothGrowthRate: 0.06,
  rothGrowthYears: 8,
  hpeInterestRate: 0.07,
  hpeInterestYears: 5,
  hpeDeductionMultiplier: 2.49,
  govtPensionTP: 0,
  govtPensionSP: 0,
  nonGovtPensionTP: 0,
  nonGovtPensionSP: 0,
};
